import { Chip, Tooltip, Box, Typography } from "@mui/material";
import ScheduleIcon from "@mui/icons-material/Schedule";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { CronExpressionParser } from "cron-parser";
import {
  formatDate,
  getTimeRemaining,
  parseCronExpression,
  getNextRunColor,
} from "../utils/dateUtils";

/**
 * Componente para mostrar el indicador de próxima ejecución del scheduler
 * @param {object} scheduler - Objeto scheduler con nextRun, cron, timezone, enabled
 * @param {boolean} showTooltip - Si mostrar tooltip con detalles (default: true)
 * @param {boolean} showIcon - Si mostrar ícono en el chip (default: true)
 * @param {string} size - Tamaño del chip: "small" | "medium" (default: "small")
 */
const SchedulerNextRunIndicator = ({
  scheduler,
  showTooltip = true,
  showIcon = true,
  size = "small",
}) => {
  // Si no hay scheduler o no está enabled
  if (!scheduler || !scheduler.enabled) {
    return (
      <Chip
        label="Desactivado"
        color="default"
        size={size}
        variant="outlined"
        icon={showIcon ? <AccessTimeIcon /> : undefined}
      />
    );
  }

  const { nextRun: nextRunFromBackend, cron, timezone } = scheduler;

  // Función para calcular el próximo nextRun a partir del cron
  const calculateNextRun = (cronExpression, tz) => {
    try {
      const options = {
        currentDate: new Date(),
        tz: tz || "America/Mexico_City",
      };
      const interval = CronExpressionParser.parse(cronExpression, options);
      return interval.next().toDate().toISOString();
    } catch (error) {
      console.error("Error parsing cron expression:", error);
      return null;
    }
  };

  // Determinar nextRun: usar el del backend o calcularlo del cron
  let nextRun = nextRunFromBackend;

  if (!nextRun && cron) {
    // Si no hay nextRun del backend pero sí hay expresión cron, calcularlo
    nextRun = calculateNextRun(cron, timezone);
  }

  // Si no hay nextRun ni cron
  if (!nextRun) {
    return (
      <Chip
        label="Sin programar"
        color="warning"
        size={size}
        variant="outlined"
        icon={showIcon ? <WarningAmberIcon /> : undefined}
      />
    );
  }

  const timeRemaining = getTimeRemaining(nextRun);
  const color = getNextRunColor(timeRemaining);
  const cronDescription = parseCronExpression(cron);

  // Chip básico sin tooltip
  const chipComponent = (
    <Chip
      label={timeRemaining?.shortText || "-"}
      color={color}
      size={size}
      variant="outlined"
      icon={showIcon ? <ScheduleIcon /> : undefined}
      sx={{
        fontWeight: timeRemaining?.isPast ? "bold" : "medium",
      }}
    />
  );

  // Si no se debe mostrar tooltip, retornar solo el chip
  if (!showTooltip) {
    return chipComponent;
  }

  // Tooltip con detalles completos
  const tooltipContent = (
    <Box sx={{ p: 0.5 }}>
      <Typography variant="body2" fontWeight="bold" gutterBottom sx={{ color: '#fff' }}>
        Próxima Ejecución
      </Typography>
      <Typography variant="body2" gutterBottom sx={{ color: '#fff' }}>
        <strong>Fecha:</strong> {formatDate(nextRun)}
      </Typography>
      <Typography variant="body2" gutterBottom sx={{ color: '#fff' }}>
        <strong>Tiempo:</strong> {timeRemaining?.text || "-"}
      </Typography>
      {cron && (
        <Typography variant="body2" gutterBottom sx={{ color: '#fff' }}>
          <strong>Frecuencia:</strong> {cronDescription}
        </Typography>
      )}
      {cron && (
        <Typography variant="caption" display="block" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          Cron: {cron}
        </Typography>
      )}
      {timezone && (
        <Typography variant="caption" display="block" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          Zona horaria: {timezone}
        </Typography>
      )}
    </Box>
  );

  return (
    <Tooltip title={tooltipContent} arrow placement="top">
      {chipComponent}
    </Tooltip>
  );
};

export default SchedulerNextRunIndicator;
